<template>
  <div>
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h2 class="subtitle">
      {{ $t("dashboard.cashback") }}
      &nbsp;&nbsp;
      <button
        class="button is-small"
        @click="isNewCashbackModalActive = true"
        style="position: relative; bottom: 2px;"
      >
        <b-icon icon="plus-circle" type="is-primary"></b-icon>
      </button>
    </h2>

    <p v-if="storeBeingManaged.cashbacks.filter(c => !c.optin).length === 0">
      {{ $t("settings.noCashbacksAtThisMoment") }}
    </p>

    <b-table
      v-else
      :data="storeBeingManaged.cashbacks.filter(c => !c.optin)"
      hoverable
      :key="tableKey"
    >
      <b-table-column
        field="type"
        :label="$t('settings.cashbackType')"
        sortable
        :custom-sort="sortCashbacks"
        v-slot="props"
      >
        {{
          props.row.type === "general"
            ? $t("settings.cashbackTypeGeneral")
            : props.row.type === "category"
            ? $t("settings.cashbackTypeCategory")
            : props.row.type === "subcategory"
            ? $t("settings.cashbackTypeSubcategory")
            : props.row.type === "product"
            ? $t("settings.cashbackTypeProduct")
            : props.row.type === "scaleProduct"
            ? $t("settings.cashbackTypeScaleProduct")
            : "?"
        }}
      </b-table-column>

      <b-table-column
        :label="$t('settings.cashbackDetail')"
        :custom-sort="sortCashbacks"
        sortable
        v-slot="props"
      >
        <span v-if="props.row.type === 'general'">
          {{ $t("settings.cashbackWholeStore") }}
        </span>

        <span v-else-if="props.row.type === 'category'">
          {{ props.row.category.name }}
        </span>

        <span v-else-if="props.row.type === 'subcategory'">
          {{ props.row.subcategory.name }}
        </span>

        <span v-else-if="props.row.type === 'product'">
          {{ `${props.row.product.name} - ${props.row.product.barcode}` }}
        </span>

        <span v-else-if="props.row.type === 'scaleProduct'">
          {{
            `${props.row.scaleProduct.name} - ${props.row.scaleProduct.code}`
          }}
        </span>
      </b-table-column>

      <b-table-column
        field="percentage"
        :label="$t('settings.cashbackPercentage')"
        sortable
        v-slot="props"
      >
        {{ props.row.percentage }}%
      </b-table-column>

      <b-table-column
        field="beginDate"
        :label="$t('settings.cashbackBeginDate')"
        sortable
        v-slot="props"
      >
        {{
          moment
            .utc(props.row.beginDate)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
      </b-table-column>

      <b-table-column
        field="endDate"
        :label="$t('settings.cashbackEndDate')"
        sortable
        v-slot="props"
      >
        {{
          moment
            .utc(props.row.endDate)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
      </b-table-column>

      <b-table-column
        field="isActive"
        :label="$t('common.status')"
        sortable
        v-slot="props"
      >
        <b-switch
          size="is-small"
          class="cashback-switch"
          type="is-primary"
          passive-type="is-secondary"
          v-model="props.row.isActive"
          @input="turnOnOffCashback(props.row)"
        ></b-switch>

        {{ props.row.isActive ? $t("common.Active") : $t("common.Inactive") }}
      </b-table-column>

      <b-table-column :label="$t('common.delete')" v-slot="props">
        <button
          class="button is-small"
          @click="confirmCashbackDeletion(props.row)"
        >
          <b-icon icon="minus-circle" type="is-primary"></b-icon>
        </button>
      </b-table-column>

      <template slot="empty">
        <table-empty-template />
      </template>
    </b-table>

    <b-modal :active.sync="isNewCashbackModalActive">
      <new-cashback-modal @increment-key="tableKey++" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../../mixins/common";
import newCashbackModal from "./NewCashbackModal";
import tableEmptyTemplate from "../../common/TableEmptyTemplate";

export default {
  mixins: [common],

  data() {
    return {
      isLoading: false,
      isNewCashbackModalActive: false,
      tableKey: 0
    };
  },

  components: {
    newCashbackModal,
    tableEmptyTemplate
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "preferences"])
  },

  methods: {
    sortCashbacks(a, b, isAsc) {
      const sortWeigth = o => {
        return o.type === "product"
          ? 1
          : o.type === "scaleProduct"
          ? 2
          : o.type === "subcategory"
          ? 3
          : o.type === "category"
          ? 4
          : o.type === "general"
          ? 5
          : 0;
      };
      a.v = sortWeigth(a);
      b.v = sortWeigth(b);
      return isAsc ? a.v - b.v : (a.v - b.v) * -1;
    },

    turnOnOffCashback(cashback) {
      this.isLoading = true;
      this.$store
        .dispatch("setCashback", {
          storeId: this.storeBeingManaged._id,
          cashbackId: cashback._id,
          isActive: cashback.isActive
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            cashback.isActive = !cashback.isActive;
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: `${this.$t("dashboard.cashback")} ${
                cashback.isActive
                  ? this.$t("common.Active")
                  : this.$t("common.Inactive")
              }`,
              position: "is-bottom",
              type: "is-primary"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmCashbackDeletion(cashback) {
      this.$buefy.dialog.confirm({
        message: `${this.$t("settings.confirmCashbackDeletion")} ${
          cashback.percentage
        }% ${
          cashback.type === "general"
            ? this.$t("settings.cashbackInEveryProduct")
            : cashback.type === "category"
            ? `${this.$t("settings.cashbackInCategory")} ${
                cashback.category.name
              }`
            : cashback.type === "subcategory"
            ? `${this.$t("settings.cashbackInSubcategory")} ${
                cashback.subcategory.name
              }`
            : cashback.type === "product"
            ? `${this.$t("settings.cashbackInProduct")} ${
                cashback.product.name
              }`
            : cashback.type === "scaleProduct"
            ? `${this.$t("settings.cashbackInProduct")} ${
                cashback.scaleProduct.name
              }`
            : ""
        }`,
        onConfirm: () => this.deleteCashback(cashback)
      });
    },

    deleteCashback(cashback) {
      this.isLoading = true;
      this.$store
        .dispatch("deleteCashback", {
          storeId: this.storeBeingManaged._id,
          cashbackId: cashback._id
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("settings.cashbackDeleted"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.tableKey++;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.cashback-switch {
  position: relative;
  top: 2px;
  left: 6px;
  -webkit-transform: scaleX(1);
  transform: scaleX(1);
}
</style>
